/**
 * EmotionCategoryMatrixManager.jsx
 * ===========================================================================
 * Admin UI for the emotion→category translation layer. Two connected tabs:
 *
 *   1. Mappings   — add / remove / re-point which emotions bucket into each of
 *      the 9 categories (was hardcoded `categoryLookup.FALLBACK_EMOTION_MAP`).
 *   2. Category prompts — the standard recording prompt + directive the Voice
 *      Actor reads for each category (was hardcoded ASEP/ASNS asset files).
 *      Edits flow into future book imports automatically; a per-category
 *      "Re-propagate" pushes an edit to EXISTING books' un-recorded samples
 *      only (recorded ones are left untouched so text never desyncs from audio).
 *
 * The category is the join between the two tabs: each category owns both the
 * emotions that map into it and the single line the actor records for it.
 *
 * Mounted behind an admin route; the server routes are also admin-gated.
 * State: `state.emotionCategoryRegistry`.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Header,
  Segment,
  Message,
  Table,
  Input,
  Dropdown,
  Button,
  Icon,
  Label,
  Confirm,
  Tab,
  Form,
  TextArea,
  Modal,
  Pagination,
  Divider,
} from 'semantic-ui-react';

import {
  fetchMatrix,
  createMapping,
  updateMapping,
  deleteMapping,
  resetMapping,
  updateCategoryPrompt,
  resetCategoryPrompt,
  repropagateCategory,
  generateSampleText,
  updateSampleText,
  repropagateEmotion,
  updateGenTemplate,
  resetGenTemplate,
  clearError,
  clearSuccess,
} from '../../slices/emotionCategoryRegistrySlice';
import './EmotionCategoryMatrixManager.css';

const EmotionCategoryMatrixManager = () => {
  const dispatch = useDispatch();
  const { categories, categoryPrompts, mappings, genTemplate, meta, loading, generating, error, success } =
    useSelector((s) => s.emotionCategoryRegistry);

  // Mappings tab state
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState(null);
  const [newEmotion, setNewEmotion] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [perPage, setPerPage] = useState(25);
  const [activePage, setActivePage] = useState(1);
  useEffect(() => { setActivePage(1); }, [search, catFilter, perPage]);

  // Sample-passage editing
  const [sampleModal, setSampleModal] = useState(null); // the mapping being edited
  const [sampleDraft, setSampleDraft] = useState({ sampleText: '', genNudge: '' });
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateDraft, setTemplateDraft] = useState({ system: '', template: '' });
  const [seedProgress, setSeedProgress] = useState(null); // { done, total }

  // Category-prompts tab state
  const [promptDrafts, setPromptDrafts] = useState({});
  const [confirmRepropagate, setConfirmRepropagate] = useState(null);

  useEffect(() => {
    dispatch(fetchMatrix());
  }, [dispatch]);

  // Seed each category's draft once (never clobber an in-progress edit).
  useEffect(() => {
    setPromptDrafts((prev) => {
      const next = { ...prev };
      for (const c of categoryPrompts || []) {
        if (next[c.category] === undefined) {
          next[c.category] = { prompt: c.prompt || '', directive: c.directive || '' };
        }
      }
      return next;
    });
  }, [categoryPrompts]);

  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ key: c, value: c, text: c })),
    [categories]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (mappings || []).filter(
      (m) => (!q || m.emotion.includes(q)) && (!catFilter || m.category === catFilter)
    );
  }, [mappings, search, catFilter]);

  const perPageOptions = [
    { key: 25, value: 25, text: '25 / page' },
    { key: 50, value: 50, text: '50 / page' },
    { key: 100, value: 100, text: '100 / page' },
    { key: 'all', value: 9999, text: 'Show all' },
  ];
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const page = Math.min(activePage, totalPages);
  const paged = filtered.slice((page - 1) * perPage, (page - 1) * perPage + perPage);

  const handleAdd = () => {
    const emotion = newEmotion.trim().toLowerCase();
    if (!emotion || !newCategory) return;
    dispatch(createMapping({ emotion, category: newCategory })).then((r) => {
      if (!r.error) {
        setNewEmotion('');
        setNewCategory('');
      }
    });
  };

  // ---- sample passages ----
  const openSampleModal = (m) => {
    setSampleModal(m);
    setSampleDraft({ sampleText: m.sampleText || '', genNudge: m.genNudge || '' });
  };

  const handleResample = () => {
    if (!sampleModal) return;
    dispatch(generateSampleText({ emotion: sampleModal.emotion, nudge: sampleDraft.genNudge })).then((r) => {
      if (!r.error && r.payload) {
        setSampleDraft({ sampleText: r.payload.sampleText || '', genNudge: r.payload.genNudge || '' });
      }
    });
  };

  const handleSaveSample = () => {
    if (!sampleModal) return;
    dispatch(updateSampleText({
      emotion: sampleModal.emotion,
      sampleText: sampleDraft.sampleText,
      genNudge: sampleDraft.genNudge,
    })).then((r) => {
      if (!r.error) setSampleModal(null); // close on success; the row updates + success banner shows
    });
  };

  // Client-orchestrated bulk seed: generate a passage for every mapping that
  // lacks one, sequentially (keeps OpenAI concurrency + server load sane; the
  // endpoint is idempotent-ish so a re-run just fills remaining gaps).
  const seedAllMissing = async () => {
    const missing = (mappings || []).filter((m) => !m.sampleText || !m.sampleText.trim());
    if (missing.length === 0) return;
    setSeedProgress({ done: 0, total: missing.length });
    for (let i = 0; i < missing.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      await dispatch(generateSampleText({ emotion: missing[i].emotion }));
      setSeedProgress({ done: i + 1, total: missing.length });
    }
    setSeedProgress(null);
  };

  const openTemplate = () => {
    setTemplateDraft({
      system: genTemplate?.system || '',
      template: genTemplate?.template || '',
    });
    setTemplateOpen(true);
  };

  const setDraft = (category, patch) =>
    setPromptDrafts((d) => ({ ...d, [category]: { ...d[category], ...patch } }));

  const handleSavePrompt = (category) => {
    const draft = promptDrafts[category];
    if (!draft || !draft.prompt.trim()) return;
    dispatch(updateCategoryPrompt({ category, prompt: draft.prompt, directive: draft.directive }));
  };

  const handleResetPrompt = (category) => {
    dispatch(resetCategoryPrompt(category)).then((r) => {
      if (!r.error && r.payload) {
        setDraft(category, { prompt: r.payload.prompt || '', directive: r.payload.directive || '' });
      }
    });
  };

  // ---- Mappings pane ----
  const mappingsPane = (
    <>
      <Header as="h4" style={{ marginBottom: 8 }}>Add mapping</Header>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          placeholder="emotion (e.g. wistful)"
          value={newEmotion}
          onChange={(_, { value }) => setNewEmotion(value)}
          style={{ minWidth: 240 }}
        />
        <Icon name="arrow right" />
        <Dropdown
          selection
          className="ecm-add-cat"
          placeholder="category"
          options={categoryOptions}
          value={newCategory}
          onChange={(_, { value }) => setNewCategory(value)}
          style={{ minWidth: 180 }}
        />
        <Button primary disabled={!newEmotion.trim() || !newCategory} onClick={handleAdd}>
          <Icon name="plus" /> Add
        </Button>
      </div>
      <Divider />

      <div className="ecm-below" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
        <Input
          icon="search"
          placeholder="Filter emotions…"
          value={search}
          onChange={(_, { value }) => setSearch(value)}
        />
        <Dropdown
          selection
          clearable
          placeholder="All categories"
          options={categoryOptions}
          value={catFilter}
          onChange={(_, { value }) => setCatFilter(value || null)}
          style={{ minWidth: 180 }}
        />
        <Button basic icon onClick={() => dispatch(fetchMatrix())} title="Reload from server">
          <Icon name="refresh" loading={loading} />
        </Button>
        <Dropdown
          selection
          compact
          options={perPageOptions}
          value={perPage}
          onChange={(_, { value }) => setPerPage(value)}
          style={{ minWidth: 120 }}
        />
        <span style={{ color: '#888' }}>
          Showing {paged.length} of {filtered.length}
          {meta?.withSample != null ? ` · ${meta.withSample} with a passage` : ''}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <Button
            size="small"
            color="purple"
            icon
            labelPosition="left"
            disabled={!!seedProgress}
            loading={!!seedProgress}
            onClick={seedAllMissing}
            title="Generate a passage for every emotion that doesn't have one yet"
          >
            <Icon name="magic" />
            {seedProgress ? `Seeding ${seedProgress.done}/${seedProgress.total}` : 'Seed missing passages'}
          </Button>
          <Button size="small" basic icon labelPosition="left" onClick={openTemplate} title="Edit the OpenAI generation prompt">
            <Icon name="edit outline" />
            Generation prompt
          </Button>
        </div>
      </div>

      <Table celled compact striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Emotion</Table.HeaderCell>
            <Table.HeaderCell collapsing>Category</Table.HeaderCell>
            <Table.HeaderCell>Sample passage</Table.HeaderCell>
            <Table.HeaderCell collapsing>Source</Table.HeaderCell>
            <Table.HeaderCell collapsing>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {paged.map((m) => (
            <Table.Row key={m.emotion}>
              <Table.Cell>{m.emotion}</Table.Cell>
              <Table.Cell>
                <Dropdown
                  inline
                  options={categoryOptions}
                  value={m.category}
                  onChange={(_, { value }) => {
                    if (value !== m.category) {
                      dispatch(updateMapping({ emotion: m.emotion, category: value }));
                    }
                  }}
                />
              </Table.Cell>
              <Table.Cell
                style={{ cursor: 'pointer', color: m.sampleText ? undefined : '#bbb' }}
                onClick={() => openSampleModal(m)}
                title="Click to edit / resample the passage"
              >
                {generating?.[m.emotion] ? (
                  <span><Icon name="circle notched" loading /> generating…</span>
                ) : m.sampleText ? (
                  <span>
                    {m.sampleText.length > 90 ? `${m.sampleText.slice(0, 90)}…` : m.sampleText}
                    {m.sampleSource === 'ai' && <Label size="tiny" basic style={{ marginLeft: 6 }}>AI</Label>}
                  </span>
                ) : (
                  <em>— no passage — click to generate/write —</em>
                )}
              </Table.Cell>
              <Table.Cell>
                {m.isDefault ? (
                  <Label size="tiny" basic>default</Label>
                ) : (
                  <Label size="tiny" color="blue">edited</Label>
                )}
              </Table.Cell>
              <Table.Cell>
                <Button size="mini" basic icon="pencil" title="Edit / resample passage" onClick={() => openSampleModal(m)} />
                {!m.isDefault && (
                  <Button
                    size="mini"
                    basic
                    icon="undo"
                    title="Reset category to the code default"
                    onClick={() => dispatch(resetMapping(m.emotion))}
                  />
                )}
                <Button
                  size="mini"
                  basic
                  color="red"
                  icon="trash"
                  title="Remove this mapping"
                  onClick={() => setConfirmDelete(m.emotion)}
                />
              </Table.Cell>
            </Table.Row>
          ))}
          {filtered.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={5} textAlign="center" style={{ color: '#999' }}>
                {loading ? 'Loading…' : 'No mappings match your filter.'}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      {totalPages > 1 && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Pagination
            activePage={page}
            totalPages={totalPages}
            onPageChange={(_, d) => setActivePage(d.activePage)}
            siblingRange={1}
            boundaryRange={1}
            size="mini"
          />
        </div>
      )}
    </>
  );

  // ---- Category prompts pane ----
  const promptsPane = (
    <>
      <Message icon info size="small">
        <Icon name="idea" />
        <Message.Content>
          The line the actor records per category. Edits apply to new imports;
          <b> Re-propagate</b> pushes to existing books' un-recorded samples only.
        </Message.Content>
      </Message>
      {(categoryPrompts || []).map((c) => {
        const draft = promptDrafts[c.category] || { prompt: c.prompt || '', directive: c.directive || '' };
        const dirty = draft.prompt !== c.prompt || draft.directive !== c.directive;
        return (
          <Segment key={c.category}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <Label color={c.isNeutral ? 'grey' : 'teal'} size="large">
                {c.category}
              </Label>
              {c.isDefault ? (
                <Label size="tiny" basic>default</Label>
              ) : (
                <Label size="tiny" color="blue">edited</Label>
              )}
              <span style={{ color: '#888', fontSize: '0.9em' }}>
                {c.emotionCount} emotion(s) map here
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <Button size="small" primary disabled={!dirty || !draft.prompt.trim()} onClick={() => handleSavePrompt(c.category)}>
                  <Icon name="save" /> Save
                </Button>
                <Button size="small" basic disabled={c.isDefault && !dirty} onClick={() => handleResetPrompt(c.category)}>
                  <Icon name="undo" /> Reset
                </Button>
                <Button size="small" basic color="orange" onClick={() => setConfirmRepropagate(c.category)}>
                  <Icon name="share square" /> Re-propagate
                </Button>
              </div>
            </div>
            <Form>
              <Form.Field inline>
                <label><span style={{ display: "block", textAlign: "right", minWidth: "200px" }}>Performance directive</span></label>
                <Input
                  value={draft.directive}
                  placeholder="e.g. Warm, open, genuinely pleased."
                  onChange={(_, { value }) => setDraft(c.category, { directive: value })}
                  disabled={c.isNeutral}
                  style={{ width: 600 }}
                />
              </Form.Field>
              <Form.Field inline>
                <label><span style={{ display: "block", textAlign: "right", minWidth: "200px" }}>Prompt (the text the actor reads)</span></label>
                <TextArea
                  value={draft.prompt}
                  rows={c.isNeutral ? 8 : 3}
                  onChange={(_, { value }) => setDraft(c.category, { prompt: value })}
                  style={{ width: 600, verticalAlign: 'top' }}
                />
              </Form.Field>
            </Form>
          </Segment>
        );
      })}
    </>
  );

  const panes = [
    { menuItem: `Mappings (${mappings?.length || 0})`, render: () => <Tab.Pane>{mappingsPane}</Tab.Pane> },
    { menuItem: `Category prompts (${categoryPrompts?.length || 0})`, render: () => <Tab.Pane>{promptsPane}</Tab.Pane> },
  ];

  return (
    <Container className="ecm-page" style={{ marginTop: '1.5rem', marginBottom: '3rem' }}>
      <Header as="h1" dividing>
        <Icon name="exchange" />
        <Header.Content>
          Emotion → Category Matrix
          <Header.Subheader>
            How the renderer buckets script emotions into the {categories?.length || 9} standard
            categories — and the standard line the Voice Actor records for each.
          </Header.Subheader>
        </Header.Content>
      </Header>

      {meta && (
        <Message size="small" info>
          <Icon name="info circle" />
          {meta.total} mappings ({meta.edited} edited) · {meta.promptsEdited || 0} prompt(s) edited ·{' '}
          {meta.populated ? 'registry live (authoritative)' : 'bootstrap (using code defaults)'}
        </Message>
      )}
      {error && (
        <Message negative onDismiss={() => dispatch(clearError())}>
          <Icon name="warning" /> {error}
        </Message>
      )}
      {success && (
        <Message positive onDismiss={() => dispatch(clearSuccess())}>
          <Icon name="check" /> {success}
        </Message>
      )}

      <Tab panes={panes} />

      <Confirm
        open={!!confirmDelete}
        header="Remove mapping?"
        content={
          confirmDelete
            ? `Remove "${confirmDelete}" from the matrix? Lines with this emotion will then fall back to the book's script classification, or neutral.`
            : ''
        }
        confirmButton="Remove"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          dispatch(deleteMapping(confirmDelete));
          setConfirmDelete(null);
        }}
      />

      <Confirm
        open={!!confirmRepropagate}
        header="Re-propagate to existing books?"
        content={
          confirmRepropagate
            ? `Push the current "${confirmRepropagate}" prompt into existing books' standard recording samples that are NOT yet recorded. Already-recorded samples are left untouched.`
            : ''
        }
        confirmButton="Re-propagate"
        onCancel={() => setConfirmRepropagate(null)}
        onConfirm={() => {
          dispatch(repropagateCategory(confirmRepropagate));
          setConfirmRepropagate(null);
        }}
      />

      {/* Per-emotion passage editor */}
      <Modal open={!!sampleModal} onClose={() => setSampleModal(null)} style={{ width: 760 }}>
        <Modal.Header>
          Sample passage — <span style={{ color: '#2185d0' }}>{sampleModal?.emotion}</span>
          {sampleModal && (
            <Label size="tiny" style={{ marginLeft: 8 }}>{sampleModal.category}</Label>
          )}
        </Modal.Header>
        <Modal.Content>
          <p style={{ color: '#888', marginTop: 0 }}>
            The artful line the actor reads for this emotion (20–40 words, global). Resample
            regenerates via OpenAI using the optional nudge; or edit the text directly.
          </p>
          <Form>
            <div style={{ marginBottom: '1em' }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>
                Nudge (optional steer for Resample)
              </label>
              <Input
                fluid
                value={sampleDraft.genNudge}
                placeholder="e.g. more restrained, older voice, a touch of gallows humor"
                onChange={(_, { value }) => setSampleDraft((d) => ({ ...d, genNudge: value }))}
              />
            </div>
            <div>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Passage</label>
              <textarea
                value={sampleDraft.sampleText}
                rows={4}
                placeholder="Click Resample to generate, or write it here."
                onChange={(e) => setSampleDraft((d) => ({ ...d, sampleText: e.target.value }))}
                style={{ width: '100%', boxSizing: 'border-box', display: 'block' }}
              />
            </div>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            color="purple"
            icon
            labelPosition="left"
            loading={!!(sampleModal && generating?.[sampleModal.emotion])}
            disabled={!!(sampleModal && generating?.[sampleModal.emotion])}
            onClick={handleResample}
          >
            <Icon name="magic" /> Resample
          </Button>
          <Button primary icon labelPosition="left" disabled={!sampleDraft.sampleText.trim()} onClick={handleSaveSample}>
            <Icon name="save" /> Save
          </Button>
          <Button
            basic
            color="orange"
            icon
            labelPosition="left"
            title="Push this passage to existing books' un-recorded samples for this emotion"
            onClick={() => sampleModal && dispatch(repropagateEmotion(sampleModal.emotion))}
          >
            <Icon name="share square" /> Re-propagate
          </Button>
          <Button onClick={() => setSampleModal(null)}>Close</Button>
        </Modal.Actions>
      </Modal>

      {/* Global generation-prompt editor */}
      <Modal open={templateOpen} onClose={() => setTemplateOpen(false)} size="large">
        <Modal.Header>Generation prompt (global)</Modal.Header>
        <Modal.Content>
          <p style={{ color: '#888', marginTop: 0 }}>
            The house-style prompt OpenAI uses for every passage. Placeholders:{' '}
            <code>{'{{emotion}}'}</code> <code>{'{{category}}'}</code>{' '}
            <code>{'{{directive}}'}</code> <code>{'{{#nudge}}…{{nudge}}…{{/nudge}}'}</code>.
          </p>
          <Form>
            {/* <Form.Field> */}
            <label>System message</label>
            <TextArea
              value={templateDraft.system}
              rows={4}
              onChange={(_, { value }) => setTemplateDraft((d) => ({ ...d, system: value }))}
            />
            {/* </Form.Field> */}
            {/* <Form.Field> */}
            <label>User template</label>
            <TextArea
              value={templateDraft.template}
              rows={14}
              onChange={(_, { value }) => setTemplateDraft((d) => ({ ...d, template: value }))}
            />
            {/* </Form.Field> */}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            basic
            onClick={() => dispatch(resetGenTemplate()).then((r) => {
              if (!r.error && r.payload) setTemplateDraft({ system: r.payload.system, template: r.payload.template });
            })}
          >
            <Icon name="undo" /> Reset to default
          </Button>
          <Button
            primary
            icon
            labelPosition="left"
            onClick={() => dispatch(updateGenTemplate(templateDraft)).then((r) => { if (!r.error) setTemplateOpen(false); })}
          >
            <Icon name="save" /> Save
          </Button>
          <Button onClick={() => setTemplateOpen(false)}>Close</Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default EmotionCategoryMatrixManager;
